import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import {
  BarChart3, TrendingUp, Users, MousePointerClick, Clock, 
  Target, Eye, Play, Pause, CheckCircle, ArrowRight,
  Calendar, RefreshCw, Download, Filter
} from 'lucide-react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  Funnel, FunnelChart, LabelList, AreaChart, Area
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444'];
const STEP_COLORS = {
  'Track Everything': '#3b82f6',
  'AI Hazard Detection': '#8b5cf6',
  'Work Anywhere': '#10b981'
};

const ProductTourAnalyticsDashboard = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [timeRange, setTimeRange] = useState('30');
  const [summary, setSummary] = useState(null);
  const [dailyStats, setDailyStats] = useState([]);
  const [stepPerformance, setStepPerformance] = useState([]);

  const fetchAnalytics = useCallback(async () => {
    try {
      const [summaryRes, dailyRes, stepRes] = await Promise.all([
        axios.get(`${API}/product-tour-analytics/summary?days=${timeRange}`),
        axios.get(`${API}/product-tour-analytics/daily-stats?days=${timeRange}`),
        axios.get(`${API}/product-tour-analytics/step-performance?days=${timeRange}`)
      ]);
      
      setSummary(summaryRes.data);
      setDailyStats(dailyRes.data.daily_stats || []);
      setStepPerformance(stepRes.data.step_performance || []);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch analytics data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [timeRange, toast]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchAnalytics();
  };

  // Prepare funnel data
  const funnelData = summary ? [
    { name: 'Viewed Tour', value: summary.conversion_funnel.viewed_tour, fill: '#3b82f6' },
    { name: 'Completed Tour', value: summary.conversion_funnel.completed_tour, fill: '#8b5cf6' },
    { name: 'Clicked CTA', value: summary.conversion_funnel.clicked_cta, fill: '#10b981' }
  ] : [];

  // Prepare step views pie chart data
  const stepViewsData = summary ? Object.entries(summary.step_views).map(([name, value]) => ({
    name,
    value,
    fill: STEP_COLORS[name] || '#94a3b8'
  })) : [];

  // Prepare time per step data
  const timePerStepData = summary ? Object.entries(summary.avg_time_per_step).map(([name, value]) => ({
    name,
    time: value,
    fill: STEP_COLORS[name] || '#94a3b8'
  })) : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="product-tour-analytics-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Play className="w-6 h-6 text-blue-600" />
            Product Tour Analytics
          </h1>
          <p className="text-slate-500 mt-1">
            Track user engagement with your landing page product tour
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-[140px]">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="14">Last 14 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600">Total Sessions</p>
                <p className="text-3xl font-bold text-blue-900">{summary?.total_sessions || 0}</p>
              </div>
              <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <p className="text-xs text-blue-600 mt-2">
              {summary?.total_events || 0} total events tracked
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-purple-600">CTA Clicks</p>
                <p className="text-3xl font-bold text-purple-900">{summary?.cta_clicks || 0}</p>
              </div>
              <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
                <MousePointerClick className="w-6 h-6 text-purple-600" />
              </div>
            </div>
            <p className="text-xs text-purple-600 mt-2">
              {summary?.conversion_funnel?.overall_conversion_rate || 0}% conversion rate
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600">Completion Rate</p>
                <p className="text-3xl font-bold text-green-900">{summary?.completion_rate || 0}%</p>
              </div>
              <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <p className="text-xs text-green-600 mt-2">
              {summary?.conversion_funnel?.completed_tour || 0} completed tours
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-amber-600">Top Engaging Step</p>
                <p className="text-lg font-bold text-amber-900 truncate">
                  {summary?.most_engaging_step || 'N/A'}
                </p>
              </div>
              <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center">
                <Target className="w-6 h-6 text-amber-600" />
              </div>
            </div>
            <p className="text-xs text-amber-600 mt-2">
              Based on avg. time spent
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs defaultValue="funnel" className="space-y-4">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="funnel" className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            Conversion Funnel
          </TabsTrigger>
          <TabsTrigger value="steps" className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            Step Performance
          </TabsTrigger>
          <TabsTrigger value="trends" className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Daily Trends
          </TabsTrigger>
        </TabsList>

        {/* Conversion Funnel Tab */}
        <TabsContent value="funnel" className="space-y-4">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Funnel Visualization */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                  Conversion Funnel
                </CardTitle>
                <CardDescription>
                  User journey from viewing to converting
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={funnelData}
                      layout="vertical"
                      margin={{ top: 20, right: 30, left: 100, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="name" type="category" width={90} />
                      <Tooltip 
                        formatter={(value) => [value, 'Users']}
                        contentStyle={{ borderRadius: '8px' }}
                      />
                      <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                        {funnelData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Funnel Metrics */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ArrowRight className="w-5 h-5 text-purple-600" />
                  Funnel Breakdown
                </CardTitle>
                <CardDescription>
                  Conversion rates between stages
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Stage 1: View → Complete */}
                <div className="p-4 bg-slate-50 rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Eye className="w-4 h-4 text-blue-600" />
                      </div>
                      <span className="font-medium">View → Complete</span>
                    </div>
                    <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                      {summary?.conversion_funnel?.view_to_complete_rate || 0}%
                    </Badge>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div 
                      className="bg-blue-500 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${summary?.conversion_funnel?.view_to_complete_rate || 0}%` }}
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    {summary?.conversion_funnel?.viewed_tour || 0} views → {summary?.conversion_funnel?.completed_tour || 0} completions
                  </p>
                </div>

                {/* Stage 2: Complete → CTA */}
                <div className="p-4 bg-slate-50 rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                        <CheckCircle className="w-4 h-4 text-purple-600" />
                      </div>
                      <span className="font-medium">Complete → CTA Click</span>
                    </div>
                    <Badge variant="secondary" className="bg-purple-100 text-purple-700">
                      {summary?.conversion_funnel?.complete_to_cta_rate || 0}%
                    </Badge>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div 
                      className="bg-purple-500 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${summary?.conversion_funnel?.complete_to_cta_rate || 0}%` }}
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    {summary?.conversion_funnel?.completed_tour || 0} completions → {summary?.conversion_funnel?.clicked_cta || 0} CTA clicks
                  </p>
                </div>

                {/* Overall Conversion */}
                <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                        <Target className="w-4 h-4 text-green-600" />
                      </div>
                      <span className="font-medium">Overall Conversion</span>
                    </div>
                    <Badge className="bg-green-600 text-white">
                      {summary?.conversion_funnel?.overall_conversion_rate || 0}%
                    </Badge>
                  </div>
                  <div className="w-full bg-green-200 rounded-full h-3">
                    <div 
                      className="bg-green-500 h-3 rounded-full transition-all duration-500"
                      style={{ width: `${summary?.conversion_funnel?.overall_conversion_rate || 0}%` }}
                    />
                  </div>
                  <p className="text-xs text-green-700 mt-2 font-medium">
                    {summary?.conversion_funnel?.viewed_tour || 0} total visitors → {summary?.conversion_funnel?.clicked_cta || 0} trial signups
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Step Performance Tab */}
        <TabsContent value="steps" className="space-y-4">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Step Views Distribution */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Eye className="w-5 h-5 text-blue-600" />
                  Step Views Distribution
                </CardTitle>
                <CardDescription>
                  Which steps are viewed most often
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={stepViewsData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name.split(' ')[0]} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={100}
                        dataKey="value"
                      >
                        {stepViewsData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Average Time per Step */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-purple-600" />
                  Average Time per Step
                </CardTitle>
                <CardDescription>
                  How long users spend on each step (seconds)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={timePerStepData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="name" 
                        angle={-45} 
                        textAnchor="end" 
                        height={60}
                        tick={{ fontSize: 12 }}
                      />
                      <YAxis label={{ value: 'Seconds', angle: -90, position: 'insideLeft' }} />
                      <Tooltip formatter={(value) => [`${value}s`, 'Avg. Time']} />
                      <Bar dataKey="time" radius={[4, 4, 0, 0]}>
                        {timePerStepData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Step Performance Cards */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-green-600" />
                Detailed Step Performance
              </CardTitle>
              <CardDescription>
                Engagement scores and metrics for each tour step
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-4">
                {stepPerformance.map((step, index) => (
                  <div 
                    key={step.step_id}
                    className="p-4 rounded-xl border-2 transition-all hover:shadow-md"
                    style={{ borderColor: Object.values(STEP_COLORS)[index] || '#e2e8f0' }}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold"
                        style={{ backgroundColor: Object.values(STEP_COLORS)[index] || '#94a3b8' }}
                      >
                        {step.step_id}
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-900">{step.step_title}</h4>
                        <p className="text-xs text-slate-500">Step {step.step_id} of 3</p>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Views</span>
                        <span className="font-medium">{step.total_views}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Clicks</span>
                        <span className="font-medium">{step.total_clicks}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Avg. Time</span>
                        <span className="font-medium">{step.avg_time_seconds}s</span>
                      </div>
                      <div className="pt-2 border-t">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-slate-600">Engagement Score</span>
                          <Badge 
                            className={`${
                              step.engagement_score >= 70 ? 'bg-green-100 text-green-700' :
                              step.engagement_score >= 40 ? 'bg-amber-100 text-amber-700' :
                              'bg-slate-100 text-slate-700'
                            }`}
                          >
                            {step.engagement_score}/100
                          </Badge>
                        </div>
                        <div className="w-full bg-slate-200 rounded-full h-2 mt-2">
                          <div 
                            className={`h-2 rounded-full transition-all duration-500 ${
                              step.engagement_score >= 70 ? 'bg-green-500' :
                              step.engagement_score >= 40 ? 'bg-amber-500' :
                              'bg-slate-400'
                            }`}
                            style={{ width: `${step.engagement_score}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                
                {stepPerformance.length === 0 && (
                  <div className="col-span-3 text-center py-8 text-slate-500">
                    <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No step performance data available yet</p>
                    <p className="text-sm">Data will appear as users interact with the product tour</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Daily Trends Tab */}
        <TabsContent value="trends" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-600" />
                Daily Engagement Trends
              </CardTitle>
              <CardDescription>
                Track product tour engagement over time
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[400px]">
                {dailyStats.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={dailyStats} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                      <defs>
                        <linearGradient id="colorSessions" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="colorViews" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="colorCTA" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="date" 
                        angle={-45} 
                        textAnchor="end"
                        height={60}
                        tick={{ fontSize: 11 }}
                      />
                      <YAxis />
                      <Tooltip 
                        contentStyle={{ borderRadius: '8px' }}
                        labelStyle={{ fontWeight: 'bold' }}
                      />
                      <Legend />
                      <Area 
                        type="monotone" 
                        dataKey="unique_sessions" 
                        name="Sessions"
                        stroke="#3b82f6" 
                        fillOpacity={1}
                        fill="url(#colorSessions)" 
                      />
                      <Area 
                        type="monotone" 
                        dataKey="step_views" 
                        name="Step Views"
                        stroke="#8b5cf6" 
                        fillOpacity={1}
                        fill="url(#colorViews)" 
                      />
                      <Area 
                        type="monotone" 
                        dataKey="cta_clicks" 
                        name="CTA Clicks"
                        stroke="#10b981" 
                        fillOpacity={1}
                        fill="url(#colorCTA)" 
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-slate-500">
                    <Calendar className="w-16 h-16 mb-4 opacity-50" />
                    <p className="text-lg font-medium">No daily data available</p>
                    <p className="text-sm">Data will appear as users interact with the product tour</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Daily Stats Table */}
          {dailyStats.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Daily Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4 font-semibold text-slate-600">Date</th>
                        <th className="text-right py-3 px-4 font-semibold text-slate-600">Sessions</th>
                        <th className="text-right py-3 px-4 font-semibold text-slate-600">Step Views</th>
                        <th className="text-right py-3 px-4 font-semibold text-slate-600">CTA Clicks</th>
                        <th className="text-right py-3 px-4 font-semibold text-slate-600">Total Events</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dailyStats.slice().reverse().map((day, index) => (
                        <tr key={day.date} className={index % 2 === 0 ? 'bg-slate-50' : ''}>
                          <td className="py-3 px-4 font-medium">{day.date}</td>
                          <td className="text-right py-3 px-4">{day.unique_sessions}</td>
                          <td className="text-right py-3 px-4">{day.step_views}</td>
                          <td className="text-right py-3 px-4">
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                              {day.cta_clicks}
                            </Badge>
                          </td>
                          <td className="text-right py-3 px-4 text-slate-500">{day.total_events}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Insights Section */}
      {summary && (summary.total_sessions > 0 || summary.cta_clicks > 0) && (
        <Card className="bg-gradient-to-r from-blue-50 via-purple-50 to-green-50 border-0">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-600" />
              Key Insights
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="p-4 bg-white/80 rounded-xl">
                <h4 className="font-semibold text-slate-900 mb-2">Most Engaging Content</h4>
                <p className="text-sm text-slate-600">
                  {summary.most_engaging_step ? (
                    <>
                      <span className="font-medium text-blue-600">{summary.most_engaging_step}</span> has the highest engagement 
                      with users spending more time on this step.
                    </>
                  ) : (
                    'Not enough data to determine the most engaging step yet.'
                  )}
                </p>
              </div>
              
              <div className="p-4 bg-white/80 rounded-xl">
                <h4 className="font-semibold text-slate-900 mb-2">Conversion Performance</h4>
                <p className="text-sm text-slate-600">
                  {summary.conversion_funnel.overall_conversion_rate >= 10 ? (
                    <span className="text-green-600 font-medium">Strong conversion rate!</span>
                  ) : summary.conversion_funnel.overall_conversion_rate >= 5 ? (
                    <span className="text-amber-600 font-medium">Moderate conversion rate.</span>
                  ) : (
                    <span className="text-slate-600">Building conversion data...</span>
                  )}
                  {' '}Your overall conversion is {summary.conversion_funnel.overall_conversion_rate}%.
                </p>
              </div>
              
              <div className="p-4 bg-white/80 rounded-xl">
                <h4 className="font-semibold text-slate-900 mb-2">Recommendation</h4>
                <p className="text-sm text-slate-600">
                  {summary.completion_rate < 50 ? (
                    'Consider making the tour more engaging to increase completion rates. Try shorter transitions or more compelling visuals.'
                  ) : summary.conversion_funnel.complete_to_cta_rate < 30 ? (
                    'Users complete the tour but don\'t click CTA. Try a stronger call-to-action or add urgency messaging.'
                  ) : (
                    'Great job! Your product tour is performing well. Keep monitoring for trends.'
                  )}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ProductTourAnalyticsDashboard;
